import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createLatestRequest,
  type LatestRequest,
  type RequestSequence,
} from '@/utils/latestRequest'

// A slow typist pauses 300 to 400 ms between letters, so 350 ms fired in the middle of a word.
// 500 ms waits out that pause and still starts the request before the operator notices a delay.
export const SEARCH_DEBOUNCE_MS = 500

type Options = {
  // The text that a page already holds when it opens, for example from the address bar.
  initial?: string
  // Runs in the same tick as the new query, so a page reset lands in the render that sends the request.
  onQueryChange?: () => void
}

/**
 * The behaviour of every search box. The text waits for a pause in the typing, a new request
 * cancels the one before it, an older answer never reaches the page, and `isSearching` is true
 * from the first keystroke until the newest answer lands.
 *
 * The page keeps its own fetch and hands it to `run`, which owns the cancellation.
 */
export function useSearch({ initial = '', onQueryChange }: Options = {}) {
  const [text, setText] = useState(initial)
  const [query, setQuery] = useState(initial.trim())
  // The query that the newest landed answer was for. Null until the first answer.
  const [answered, setAnswered] = useState<string | null>(null)
  const [inFlight, setInFlight] = useState(false)

  const sequence = useRef<RequestSequence>({ current: 0 })
  const latest = useRef<LatestRequest | null>(null)
  const queryRef = useRef(query)
  queryRef.current = query
  const onQueryChangeRef = useRef(onQueryChange)
  onQueryChangeRef.current = onQueryChange

  const changeQuery = useCallback((next: string) => {
    if (next === queryRef.current) return
    queryRef.current = next
    setQuery(next)
    onQueryChangeRef.current?.()
  }, [])

  useEffect(() => {
    const trimmed = text.trim()
    if (trimmed === query) return
    const timer = window.setTimeout(
      () => changeQuery(trimmed),
      SEARCH_DEBOUNCE_MS
    )
    return () => window.clearTimeout(timer)
  }, [text, query, changeQuery])

  useEffect(() => () => latest.current?.cancel(), [])

  // Enter skips the wait.
  const searchNow = useCallback(
    (value: string) => {
      setText(value)
      changeQuery(value.trim())
    },
    [changeQuery]
  )

  const run = useCallback(
    async <T>(
      task: (signal: AbortSignal) => Promise<T>,
      onResult: (value: T) => void,
      onError?: (error: unknown) => void
    ) => {
      latest.current?.cancel()
      const request = createLatestRequest(sequence.current)
      latest.current = request
      const forQuery = queryRef.current
      setInFlight(true)
      try {
        const value = await task(request.signal)
        if (request.isCurrent()) onResult(value)
      } catch (error) {
        if (request.isCurrent()) onError?.(error)
      } finally {
        if (request.isCurrent()) {
          setInFlight(false)
          setAnswered(forQuery)
        }
      }
    },
    []
  )

  return {
    text,
    query,
    setText,
    searchNow,
    run,
    isSearching: inFlight || text.trim() !== answered,
  }
}
